import { fmtDate, publicAppUrl } from "./api";

/*
 * Shareable cards for Instagram posts (1080x1350, 4:5) and Stories / WhatsApp Status (1080x1920,
 * via toStory), drawn on a canvas so they work offline and need no extra library.
 * Cards: match-day report, a player's "My games", and club performance leaderboards.
 */
const W = 1080, H = 1350, PAD = 72;
const GREEN = "#7CC242";
const FONT_HEAD = "Manrope, system-ui, sans-serif";
const FONT_BODY = "'DM Sans', system-ui, sans-serif";

const loadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Draw text, shrinking the font (down to minSize) and then truncating with … so it fits maxWidth. */
function fitText(ctx, text, x, y, maxWidth, { size, minSize = size * 0.7, weight = 700, font = FONT_BODY, color = "#fff", align = "left" }) {
  let s = size;
  ctx.font = `${weight} ${s}px ${font}`;
  while (ctx.measureText(text).width > maxWidth && s > minSize) {
    s -= 1;
    ctx.font = `${weight} ${s}px ${font}`;
  }
  let t = text;
  while (ctx.measureText(t).width > maxWidth && t.length > 1) t = t.slice(0, -2) + "…";
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.fillText(t, x, y);
  ctx.textAlign = "left";
}

const scoreLine = (m, team) => {
  const s = m.scores.find((x) => x.team === team);
  return s ? `${s.runs}/${s.wickets} (${s.overs})` : "—";
};

function paintBackground(ctx, w, h) {
  ctx.fillStyle = "#070807";
  ctx.fillRect(0, 0, w, h);
  for (const [x, y, rad, col] of [[w * 0.9, 60, 760, "rgba(124,194,66,0.30)"], [0, h, 820, "rgba(46,125,50,0.38)"]]) {
    const g = ctx.createRadialGradient(x, y, 0, x, y, rad);
    g.addColorStop(0, col); g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
  }
}

function drawWordmark(ctx, logo, x, y) {
  if (logo) {
    ctx.save(); roundRect(ctx, x, y, 76, 76, 18); ctx.clip(); ctx.drawImage(logo, x, y, 76, 76); ctx.restore();
  }
  ctx.font = `800 40px ${FONT_HEAD}`;
  ctx.fillStyle = "#fff"; ctx.fillText("CRIC", x + 96, y + 52);
  ctx.fillStyle = GREEN; ctx.fillText("SO", x + 96 + ctx.measureText("CRIC").width, y + 52);
}

/** New 1080x1350 card: fonts loaded, branded background, logo + wordmark and a tag pill at the top. */
async function startCard(tag) {
  await Promise.all([
    document.fonts?.load(`800 40px Manrope`), document.fonts?.load(`700 30px "DM Sans"`), document.fonts?.load(`500 30px "DM Sans"`),
  ].filter(Boolean)).catch(() => {});
  const logo = await loadImage("/icon-192.png");
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.textBaseline = "alphabetic";
  paintBackground(ctx, W, H);
  drawWordmark(ctx, logo, PAD, PAD);
  ctx.font = `800 22px ${FONT_HEAD}`;
  const tw = ctx.measureText(tag).width + 40;
  roundRect(ctx, W - PAD - tw, PAD + 16, tw, 46, 23); ctx.fillStyle = GREEN; ctx.fill();
  ctx.fillStyle = "#0b0b0b"; ctx.fillText(tag, W - PAD - tw + 20, PAD + 47);
  return { c, ctx };
}

/** Kicker (club name), big title and a sub line; returns the y after them. */
function drawTitle(ctx, kicker, title, sub) {
  let y = PAD + 150;
  if (kicker) fitText(ctx, kicker.toUpperCase(), PAD, y, W - 2 * PAD, { size: 28, weight: 800, font: FONT_HEAD, color: GREEN });
  y += 74;
  fitText(ctx, title, PAD, y, W - 2 * PAD, { size: 72, minSize: 48, weight: 800, font: FONT_HEAD });
  y += 50;
  if (sub) fitText(ctx, sub, PAD, y, W - 2 * PAD, { size: 30, weight: 500, color: "rgba(255,255,255,0.7)" });
  return y + 44;
}

/** Row of big-number tiles, e.g. [["MATCHES", 12], ["RUNS", 340]]; returns the y below. */
function drawTiles(ctx, y, tiles) {
  const gap = 18, tileW = (W - 2 * PAD - (tiles.length - 1) * gap) / tiles.length;
  tiles.forEach(([label, val], i) => {
    const x = PAD + i * (tileW + gap);
    roundRect(ctx, x, y, tileW, 120, 26); ctx.fillStyle = "rgba(255,255,255,0.07)"; ctx.fill();
    fitText(ctx, String(val), x + tileW / 2, y + 68, tileW - 24, { size: 52, weight: 800, font: FONT_HEAD, align: "center" });
    fitText(ctx, label, x + tileW / 2, y + 100, tileW - 24, { size: 20, weight: 700, color: "rgba(255,255,255,0.55)", align: "center" });
  });
  return y + 150;
}

const FOOTER_TOP = H - PAD - 100;

/** "Scored live with Cricso • link" and "Powered by HMT Technologies", centred. */
function drawFooter(ctx, top = FOOTER_TOP) {
  ctx.fillStyle = "rgba(255,255,255,0.12)"; ctx.fillRect(PAD, top, W - 2 * PAD, 2);
  const host = publicAppUrl().replace(/^https?:\/\//, "");
  fitText(ctx, `Scored live with Cricso  •  ${host}`, W / 2, top + 46, W - 2 * PAD,
    { size: 26, weight: 600, color: "rgba(255,255,255,0.8)", align: "center" });
  const p1 = "Powered by ", p2 = "HMT Technologies";
  ctx.font = `500 23px ${FONT_BODY}`; const w1 = ctx.measureText(p1).width;
  ctx.font = `800 23px ${FONT_BODY}`; const w2 = ctx.measureText(p2).width;
  const px = (W - w1 - w2) / 2;
  ctx.font = `500 23px ${FONT_BODY}`; ctx.fillStyle = "rgba(255,255,255,0.55)"; ctx.fillText(p1, px, top + 84);
  ctx.font = `800 23px ${FONT_BODY}`; ctx.fillStyle = "#fff"; ctx.fillText(p2, px + w1, top + 84);
}

// "1 run", "2 runs" — leaderboard units
const UNIT_ONE = { pts: "pt", runs: "run", wkts: "wkt", dismissals: "dismissal", wins: "win", "6s": "six", days: "day" };
const UNIT_MANY = { "6s": "sixes" };
export const withUnit = (n, unit) => `${n} ${n === 1 ? UNIT_ONE[unit] || unit : UNIT_MANY[unit] || unit}`;

const toPng = (c) => new Promise((resolve) => c.toBlob(resolve, "image/png"));

/** Returns a PNG Blob of the card. `r` is the /polls/:id/report payload. */
export async function drawReportCard(r, { clubName } = {}) {
  const { c, ctx } = await startCard("MATCH DAY REPORT");

  let y = drawTitle(ctx, clubName, r.poll.title || "Match day", [fmtDate(r.poll.date), r.poll.ground].filter(Boolean).join("  •  "));
  y = drawTiles(ctx, y, [["MATCHES", r.matches_played], ["RUNS", r.total_runs], ["WICKETS", r.total_wickets],
    ["6s / 4s", `${r.total_sixes}/${r.total_fours}`]]);

  // Awards
  const potd = r.player_of_the_day, bat = r.batter_of_the_day, bowl = r.bowler_of_the_day;
  if (potd) {
    roundRect(ctx, PAD, y, W - 2 * PAD, 150, 30);
    const g = ctx.createLinearGradient(PAD, y, W - PAD, y + 150);
    g.addColorStop(0, "rgba(124,194,66,0.32)"); g.addColorStop(1, "rgba(46,125,50,0.14)");
    ctx.fillStyle = g; ctx.fill();
    ctx.font = `64px ${FONT_BODY}`; ctx.fillStyle = "#fff"; ctx.fillText("🏆", PAD + 30, y + 98);
    fitText(ctx, "PLAYER OF THE DAY", PAD + 130, y + 52, 600, { size: 22, weight: 800, font: FONT_HEAD, color: GREEN });
    fitText(ctx, potd.name, PAD + 130, y + 100, W - 2 * PAD - 160, { size: 48, minSize: 34, weight: 800, font: FONT_HEAD });
    const bits = [potd.innings && `${potd.runs} runs (${potd.balls})`, potd.wickets && `${potd.wickets} wkt${potd.wickets === 1 ? "" : "s"}`,
      (potd.catches + potd.run_outs + potd.stumpings) && `${potd.catches + potd.run_outs + potd.stumpings} in the field`].filter(Boolean);
    fitText(ctx, bits.join("  •  "), PAD + 130, y + 132, W - 2 * PAD - 160, { size: 24, weight: 500, color: "rgba(255,255,255,0.75)" });
    y += 170;
  }
  if (bat || bowl) {
    const half = (W - 2 * PAD - 20) / 2;
    [[bat, "BATSMAN OF THE DAY", bat && `${bat.runs} runs (${bat.balls})  •  ${bat.fours}×4 ${bat.sixes}×6`],
     [bowl, "BOWLER OF THE DAY", bowl && `${bowl.wickets}/${bowl.bowl_runs} (${bowl.overs})  •  Econ ${bowl.economy.toFixed(1)}`]]
      .forEach(([p, label, line], i) => {
        const x = PAD + i * (half + 20);
        roundRect(ctx, x, y, half, 150, 28); ctx.fillStyle = "rgba(255,255,255,0.07)"; ctx.fill();
        ctx.font = `34px ${FONT_BODY}`; ctx.fillStyle = "#fff"; ctx.fillText("🏆", x + 24, y + 54);
        fitText(ctx, label, x + 74, y + 48, half - 98, { size: 19, weight: 800, font: FONT_HEAD, color: GREEN });
        fitText(ctx, p ? p.name : "—", x + 24, y + 100, half - 48, { size: 36, minSize: 26, weight: 800, font: FONT_HEAD });
        if (line) fitText(ctx, line, x + 24, y + 132, half - 48, { size: 22, minSize: 17, weight: 500, color: "rgba(255,255,255,0.72)" });
      });
    y += 178;
  }

  // Results (as many as fit above the footer)
  const footerTop = FOOTER_TOP;
  fitText(ctx, "RESULTS", PAD, y + 8, 400, { size: 22, weight: 800, font: FONT_HEAD, color: "rgba(255,255,255,0.55)" });
  y += 30;
  const rowH = 96;
  const room = Math.max(0, Math.floor((footerTop - 24 - y) / rowH));
  const shown = r.matches.slice(0, room);
  shown.forEach((m) => {
    roundRect(ctx, PAD, y, W - 2 * PAD, rowH - 14, 22); ctx.fillStyle = "rgba(255,255,255,0.05)"; ctx.fill();
    const live = m.status !== "completed";
    fitText(ctx, `${m.teams[0]} ${scoreLine(m, 0)}   v   ${m.teams[1]} ${scoreLine(m, 1)}`, PAD + 24, y + 38, W - 2 * PAD - 48,
      { size: 28, minSize: 21, weight: 700 });
    fitText(ctx, live ? "● Live" : m.result || "", PAD + 24, y + 70, W - 2 * PAD - 48,
      { size: 23, weight: 600, color: live ? "#f87171" : GREEN });
    y += rowH;
  });
  if (r.matches.length > shown.length) {
    fitText(ctx, `+ ${r.matches.length - shown.length} more match${r.matches.length - shown.length === 1 ? "" : "es"}`, PAD, y + 4, 600,
      { size: 22, weight: 600, color: "rgba(255,255,255,0.55)" });
  }

  drawFooter(ctx);
  return toPng(c);
}

/** Short Instagram caption (Instagram ignores text shared alongside an image, so it's copied instead). */
export function reportCaption(r, clubName) {
  const lines = [`🏏 ${r.poll.title || "Match day"} — ${fmtDate(r.poll.date)}${clubName ? ` | ${clubName}` : ""}`, ""];
  r.matches.filter((m) => m.result).forEach((m) => lines.push(`✅ ${m.result}`));
  if (r.player_of_the_day) lines.push(`🏆 Player of the day: ${r.player_of_the_day.name}`);
  if (r.batter_of_the_day) lines.push(`🏏 Batsman of the day: ${r.batter_of_the_day.name}`);
  if (r.bowler_of_the_day) lines.push(`🎯 Bowler of the day: ${r.bowler_of_the_day.name}`);
  lines.push("", "Scored live with Cricso 📲", "#cricket #cricketclub #weekendcricket #matchday #cricso");
  return lines.join("\n");
}


// ---------------------------------------------------------------------------------------------
// My games (one player's record)

const OUTCOME_CHIP = {
  won: ["WON", "#7CC242"], lost: ["LOST", "#f87171"], tied: ["TIED", "#fbbf24"], common: ["COMMON", "#7dd3fc"],
};

/** `matches` is the /stats/me/matches list (newest first). */
export async function drawMyGamesCard({ name, clubName, matches }) {
  const { c, ctx } = await startCard("MY GAMES");
  const bats = matches.flatMap((m) => m.batting), bowls = matches.flatMap((m) => m.bowling);
  const runs = bats.reduce((a, b) => a + b.runs, 0), wkts = bowls.reduce((a, b) => a + b.wickets, 0);
  const won = matches.filter((m) => m.outcome === "won").length;
  const oldest = matches[matches.length - 1]?.date;

  let y = drawTitle(ctx, clubName, name || "My games",
    `${matches.length} match${matches.length === 1 ? "" : "es"}${oldest ? `  •  since ${fmtDate(oldest)}` : ""}`);
  y = drawTiles(ctx, y, [["MATCHES", matches.length], ["WON", won], ["RUNS", runs], ["WICKETS", wkts]]);

  // Highlights
  const top = bats.reduce((b, x) => (!b || x.runs > b.runs || (x.runs === b.runs && !x.out) ? x : b), null);
  const best = bowls.reduce((b, x) => (!b || x.wickets > b.wickets || (x.wickets === b.wickets && x.runs < b.runs) ? x : b), null);
  const field = matches.reduce((a, m) => a + m.fielding.catches + m.fielding.run_outs + m.fielding.stumpings, 0);
  const balls = bats.reduce((a, b) => a + b.balls, 0);
  const hl = [
    ["🏏", "BEST SCORE", top ? `${top.runs}${top.out ? "" : "*"} (${top.balls})` : "—"],
    ["🎯", "BEST BOWLING", best && best.wickets ? `${best.wickets}/${best.runs}` : "—"],
    ["🧤", "FIELDING", `${field} out${field === 1 ? "" : "s"}`],
    ["⚡", "STRIKE RATE", balls ? (runs * 100 / balls).toFixed(0) : "—"],
  ];
  const hw = (W - 2 * PAD - 3 * 18) / 4;
  hl.forEach(([emoji, label, val], i) => {
    const x = PAD + i * (hw + 18);
    roundRect(ctx, x, y, hw, 132, 26); ctx.fillStyle = "rgba(124,194,66,0.14)"; ctx.fill();
    ctx.font = `30px ${FONT_BODY}`; ctx.fillStyle = "#fff"; ctx.fillText(emoji, x + 20, y + 44);
    fitText(ctx, label, x + 20, y + 76, hw - 32, { size: 17, weight: 800, font: FONT_HEAD, color: GREEN });
    fitText(ctx, val, x + 20, y + 114, hw - 32, { size: 32, minSize: 22, weight: 800, font: FONT_HEAD });
  });
  y += 168;

  // Recent matches
  fitText(ctx, "RECENT MATCHES", PAD, y + 8, 500, { size: 22, weight: 800, font: FONT_HEAD, color: "rgba(255,255,255,0.55)" });
  y += 30;
  const rowH = 100, room = Math.max(0, Math.floor((FOOTER_TOP - 24 - y) / rowH));
  matches.slice(0, room).forEach((m) => {
    roundRect(ctx, PAD, y, W - 2 * PAD, rowH - 14, 22); ctx.fillStyle = "rgba(255,255,255,0.05)"; ctx.fill();
    const [label, col] = m.status !== "completed" ? ["LIVE", "#f87171"] : OUTCOME_CHIP[m.outcome] || ["—", "#999"];
    ctx.font = `800 18px ${FONT_HEAD}`;
    const cw = ctx.measureText(label).width + 28;
    roundRect(ctx, W - PAD - 24 - cw, y + 16, cw, 34, 17); ctx.fillStyle = col; ctx.fill();
    ctx.fillStyle = "#0b0b0b"; ctx.fillText(label, W - PAD - 24 - cw + 14, y + 40);
    fitText(ctx, `${fmtDate(m.date)}  •  ${m.teams[0]} vs ${m.teams[1]}`, PAD + 24, y + 40, W - 2 * PAD - 72 - cw,
      { size: 26, minSize: 20, weight: 700 });
    const bat = m.batting.map((b) => `${b.runs}${b.out ? "" : "*"} (${b.balls})`).join(", ");
    const bowl = m.bowling.map((b) => `${b.wickets}/${b.runs} (${b.overs})`).join(", ");
    fitText(ctx, [bat && `Bat ${bat}`, bowl && `Bowl ${bowl}`].filter(Boolean).join("   •   ") || "Fielded", PAD + 24, y + 72,
      W - 2 * PAD - 48, { size: 23, weight: 500, color: "rgba(255,255,255,0.75)" });
    y += rowH;
  });

  drawFooter(ctx);
  return toPng(c);
}

export function myGamesCaption({ name, clubName, matches }) {
  const runs = matches.flatMap((m) => m.batting).reduce((a, b) => a + b.runs, 0);
  const wkts = matches.flatMap((m) => m.bowling).reduce((a, b) => a + b.wickets, 0);
  const won = matches.filter((m) => m.outcome === "won").length;
  return [`🏏 My cricket${clubName ? ` with ${clubName}` : ""}`, "",
    `${matches.length} matches • ${won} wins • ${runs} runs • ${wkts} wickets`, "",
    `Tracked on Cricso 📲${name ? ` — ${name}` : ""}`, "#cricket #cricketlife #weekendcricket #cricso"].join("\n");
}

// ---------------------------------------------------------------------------------------------
// Club performance (leaderboards)

const BOARD_EMOJI = { mvp: "🏆", batting: "🏏", bowling: "🎯", fielding: "🧤", captain: "👑", sixes: "💥", attendance: "📅" };

/** `data` is the /stats/club payload. */
export async function drawClubCard({ clubName, periodLabel, data }) {
  const { c, ctx } = await startCard("CLUB PERFORMANCE");
  let y = drawTitle(ctx, `Leaderboards  •  ${periodLabel}`, clubName || "Club",
    [`${data.matches} match${data.matches === 1 ? "" : "es"}`, data.match_days > 0 && `${data.match_days} match day${data.match_days === 1 ? "" : "s"}`]
      .filter(Boolean).join("  •  "));

  const t = data.totals;
  if (t) {
    const items = [["RUNS", t.runs], ["WICKETS", t.wickets], ["SIXES", t.sixes], ["FOURS", t.fours], ["CATCHES", t.catches]];
    const g = 12, bw = (W - 2 * PAD - g * (items.length - 1)) / items.length, bh = 104;
    items.forEach(([label, val], i) => {
      const x = PAD + i * (bw + g);
      roundRect(ctx, x, y, bw, bh, 22);
      ctx.fillStyle = "rgba(255,255,255,0.07)"; ctx.fill();
      fitText(ctx, String(val ?? 0), x + bw / 2, y + 56, bw - 16, { size: 42, minSize: 26, weight: 800, font: FONT_HEAD, align: "center" });
      fitText(ctx, label, x + bw / 2, y + 88, bw - 12, { size: 17, minSize: 12, weight: 800, font: FONT_HEAD, color: GREEN, align: "center" });
    });
    y += bh + 20;
  }

  const boards = data.boards.filter((b) => b.rows.length > 0).slice(0, 6);
  const cols = 2, rows = Math.max(1, Math.ceil(boards.length / cols)), gap = 20;
  const tw = (W - 2 * PAD - gap) / cols;
  const th = Math.min(250, (FOOTER_TOP - 30 - y - (rows - 1) * gap) / rows);
  boards.forEach((b, i) => {
    const x = PAD + (i % cols) * (tw + gap), ty = y + Math.floor(i / cols) * (th + gap);
    const lead = b.rows[0];
    roundRect(ctx, x, ty, tw, th, 28);
    ctx.fillStyle = i === 0 ? "rgba(124,194,66,0.22)" : "rgba(255,255,255,0.07)"; ctx.fill();
    ctx.font = `30px ${FONT_BODY}`; ctx.fillStyle = "#fff"; ctx.fillText(BOARD_EMOJI[b.key] || "⭐", x + 24, ty + 48);
    fitText(ctx, b.title.toUpperCase(), x + 70, ty + 44, tw - 94, { size: 19, weight: 800, font: FONT_HEAD, color: GREEN });
    fitText(ctx, lead.name, x + 24, ty + 98, tw - 48, { size: 36, minSize: 26, weight: 800, font: FONT_HEAD });
    fitText(ctx, withUnit(lead.value, b.unit), x + 24, ty + 138, tw - 48, { size: 28, weight: 700, color: "#fff" });
    const rest = b.rows.slice(1, 3).map((r, j) => `${j + 2}. ${r.name.split(" ")[0]} ${r.value}`).join("    ");
    if (rest && th > 190) fitText(ctx, rest, x + 24, ty + th - 30, tw - 48, { size: 22, weight: 500, color: "rgba(255,255,255,0.6)" });
  });

  drawFooter(ctx);
  return toPng(c);
}

export function clubCaption({ clubName, periodLabel, data }) {
  const lines = [`🏆 ${clubName || "Club"} — leaderboards (${periodLabel})`, ""];
  const t = data.totals;
  if (t) lines.push(`📈 ${t.runs} runs • ${t.wickets} wickets • ${t.sixes} sixes • ${t.fours} fours • ${t.catches} catches`, "");
  data.boards.filter((b) => b.rows.length).slice(0, 6).forEach((b) =>
    lines.push(`${BOARD_EMOJI[b.key] || "⭐"} ${b.title}: ${b.rows[0].name} (${withUnit(b.rows[0].value, b.unit)})`));
  lines.push("", "Scored live with Cricso 📲", "#cricket #cricketclub #leaderboard #weekendcricket #cricso");
  return lines.join("\n");
}

// ---------------------------------------------------------------------------------------------
// Story / WhatsApp Status version (9:16) of any card

/** Places a 1080x1350 card on a 1080x1920 branded background for Stories and WhatsApp Status. */
export async function toStory(cardBlob) {
  const url = URL.createObjectURL(cardBlob);
  const card = await loadImage(url);
  URL.revokeObjectURL(url);
  const SH = 1920, top = (SH - H) / 2;
  const c = document.createElement("canvas");
  c.width = W; c.height = SH;
  const ctx = c.getContext("2d");
  paintBackground(ctx, W, SH);
  fitText(ctx, "Shared from Cricso 🏏", W / 2, top - 110, W - 2 * PAD, { size: 34, weight: 700, color: "rgba(255,255,255,0.85)", align: "center" });
  ctx.save(); roundRect(ctx, 24, top - 40, W - 48, H + 80, 48); ctx.clip();
  ctx.drawImage(card, 0, top);
  ctx.restore();
  ctx.strokeStyle = "rgba(124,194,66,0.45)"; ctx.lineWidth = 3;
  roundRect(ctx, 24, top - 40, W - 48, H + 80, 48); ctx.stroke();
  const host = publicAppUrl().replace(/^https?:\/\//, "");
  ctx.font = `800 30px ${FONT_HEAD}`;
  const pill = ctx.measureText(host).width + 60;
  roundRect(ctx, (W - pill) / 2, SH - 190, pill, 64, 32); ctx.fillStyle = GREEN; ctx.fill();
  fitText(ctx, host, W / 2, SH - 147, pill - 40, { size: 30, weight: 800, font: FONT_HEAD, color: "#0b0b0b", align: "center" });
  return toPng(c);
}
